import React from 'react'
import {
    Menu,
    MenuItem,
    Typography,
    Box,
    Divider,
    Tooltip,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
    Add as AddIcon,
    Flag as CampaignIcon,
    Info as InfoIcon,
} from '@material-ui/icons'
import { Campaign, Prospect } from '../../../../models/Outreach/types'

const useStyles = makeStyles(() => ({
    createCampaignMenuItem: {
        backgroundColor: '#f0f9ff',
        padding: '12px 16px',
        color: '#3b82f6',
        fontWeight: 600,
        '&:hover': {
            backgroundColor: '#e0f2fe',
        },
    },
    campaignMenuItem: {
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
            backgroundColor: '#f8fafc',
        },
    },
    campaignMenuDivider: {
        margin: '8px 0',
    },
    invalidCampaignMenuItem: {
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#fef2f2',
        color: '#991b1b',
        opacity: 0.8,
        cursor: 'not-allowed',
    },
}))

interface CampaignMenuProps {
    anchorEl: HTMLElement | null
    open: boolean
    onClose: () => void
    prospect: Prospect
    campaigns: Campaign[] | undefined
    isLoadingCampaigns: boolean
    onSelectCampaign: (campaign: Campaign, prospectId: number) => void
    onCreateCampaign: () => void
    canProspectBeAddedToCampaign: (
        prospect: Prospect,
        campaign: Campaign,
    ) => { valid: boolean; reason?: string }
    isAddingToCampaign: boolean
}

export const CampaignMenu: React.FC<CampaignMenuProps> = ({
    anchorEl,
    open,
    onClose,
    prospect,
    campaigns,
    isLoadingCampaigns,
    onSelectCampaign,
    onCreateCampaign,
    canProspectBeAddedToCampaign,
    isAddingToCampaign,
}) => {
    const classes = useStyles()

    return (
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            PaperProps={{
                style: {
                    minWidth: 200,
                    borderRadius: 8,
                    boxShadow:
                        '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                },
            }}
        >
            {/* Create Campaign - At the top */}
            <MenuItem
                onClick={onCreateCampaign}
                className={classes.createCampaignMenuItem}
            >
                <Box display="flex" alignItems="center">
                    <AddIcon
                        style={{
                            fontSize: 18,
                            marginRight: 10,
                        }}
                    />
                    Create Campaign
                </Box>
            </MenuItem>
            <Divider className={classes.campaignMenuDivider} />

            {/* Campaign List */}
            {isLoadingCampaigns ? (
                <MenuItem disabled>
                    <Typography
                        variant="body2"
                        style={{
                            color: '#6b7280',
                        }}
                    >
                        Loading campaigns...
                    </Typography>
                </MenuItem>
            ) : campaigns && campaigns.length > 0 ? (
                <>
                    <Box
                        style={{
                            padding: '8px 16px 4px',
                        }}
                    >
                        <Typography
                            variant="caption"
                            style={{
                                color: '#6b7280',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                            }}
                        >
                            Select Campaign
                        </Typography>
                    </Box>
                    {campaigns.map((campaign: Campaign) => {
                        const validation = canProspectBeAddedToCampaign(
                            prospect,
                            campaign,
                        )
                        const isValid = validation.valid

                        return (
                            <Tooltip
                                key={campaign.id}
                                title={
                                    !isValid
                                        ? validation.reason ||
                                          'Cannot add prospect to this campaign'
                                        : ''
                                }
                                arrow
                            >
                                <MenuItem
                                    onClick={() => {
                                        if (isValid && !isAddingToCampaign) {
                                            onSelectCampaign(
                                                campaign,
                                                prospect.id,
                                            )
                                        }
                                    }}
                                    className={
                                        isValid
                                            ? classes.campaignMenuItem
                                            : classes.invalidCampaignMenuItem
                                    }
                                    disabled={!isValid || isAddingToCampaign}
                                >
                                    <CampaignIcon
                                        style={{
                                            fontSize: 16,
                                            color: isValid
                                                ? '#3b82f6'
                                                : '#ef4444',
                                            marginRight: 8,
                                        }}
                                    />
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        style={{
                                            flex: 1,
                                            minWidth: 0,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            style={{
                                                fontWeight: 500,
                                                color: isValid
                                                    ? 'inherit'
                                                    : '#991b1b',
                                            }}
                                        >
                                            {campaign.name}
                                        </Typography>
                                        {!isValid && validation.reason && (
                                            <Typography
                                                variant="caption"
                                                style={{
                                                    fontSize: '0.7rem',
                                                    color: '#991b1b',
                                                    marginTop: 2,
                                                    lineHeight: 1.2,
                                                }}
                                            >
                                                {validation.reason}
                                            </Typography>
                                        )}
                                    </Box>
                                    {!isValid && (
                                        <InfoIcon
                                            style={{
                                                fontSize: 18,
                                                color: '#ef4444',
                                                marginLeft: 8,
                                                flexShrink: 0,
                                            }}
                                        />
                                    )}
                                </MenuItem>
                            </Tooltip>
                        )
                    })}
                </>
            ) : (
                <MenuItem disabled>
                    <Typography
                        variant="body2"
                        style={{
                            color: '#6b7280',
                        }}
                    >
                        No campaigns available
                    </Typography>
                </MenuItem>
            )}
        </Menu>
    )
}
